import { action, thunk, computed, actionOn } from 'easy-peasy';
import { csvParse } from 'd3-dsv';
import orderBy from 'lodash.orderby';

const initialValue = '81302';

function parseRow(row) {
  const sector = +row.Berufsbereich;

  return {
    value: row.Berufsgattung,
    label: row.Beruf,
    qualification: row.Qualifikation,
    sector: sector === 7 ? 6 : sector,

    wage_all: +row['Median-Vollzeitlohn'],
    wage_male: +row['Männer Lohn'],
    wage_female: +row['Frauen Lohn'],
    wage_under40: +row['unter 30 Jahren Lohn'],
    wage_over40: +row['über 30 Jahren Lohn'],
    wage_west: +row['West Lohn'],
    wage_south: +row['Süd Lohn'],
    wage_east: +row['Ost Lohn'],

    workers_all: +row['Beschäftige'],
    workers_male: +row['Männer Beschäftigte'],
    workers_female: +row['Frauen Beschäftigte'],
    workers_under40: +row['unter 30 Jahren Beschäftigte'],
    workers_over40: +row['über 30 Jahren Beschäftigte'],
    workers_west: +row['West Beschäftigte'],
    workers_south: +row['Süd Beschäftigte'],
    workers_east: +row['Ost Beschäftigte'],

    unemployment_abs: +row['Arbeitslose je offene Stelle (Engpassrelation)'],
    unemployment_rel: +row['Arbeitslose je Beschäftigtem'],
    vacancies: +row['Gemeldete offene Stellen'],
  };
}

function getHighlight(data, state) {
  if (!state.filterQualification && !state.filterSector) {
    return true;
  }

  const filterSector = state.filterSector === data.sector;
  const filterQualification = state.filterQualification === data.qualification;

  if (!state.filterSector) {
    return filterQualification;
  }

  if (!state.filterQualification) {
    return filterSector;
  }

  return filterQualification && filterSector;
}

function filterNullValue(d) {
  return !Number.isNaN(d.x) && !Number.isNaN(d.y);
}

const filterVacancies = (value) => (d) => {
  return d.vacancies >= 100 || value === d.value;
};

function getChartData(state) {
  if (!state.data) {
    return null;
  }

  const yColumn = `wage_${state.filterDemographics}`;
  const rColumn = `workers_${state.filterDemographics}`;

  const currentData = state.data
    .map((d) => {
      d.x = d[state.xColumn];
      d.y = d[yColumn];
      d.r = d[rColumn];
      d.selected = d.value === state.value;
      d.highlight = getHighlight(d, state);

      return d;
    })
    .filter(filterVacancies(state.value))
    .filter(filterNullValue);

  return orderBy(
    currentData,
    ['selected', 'highlight', 'r'],
    ['asc', 'asc', 'desc']
  );
}

export default {
  value: initialValue,
  searchValue: computed((state) => state.dataMap.get(state.value)),
  notFound: computed(
    (state) => state.searchValue && !filterNullValue(state.searchValue)
  ),
  data: null,
  showDefaultLabels: false,
  chartData: computed(getChartData),
  showFullXScale: false,
  toggleXScale: action((state) => {
    state.showFullXScale = !state.showFullXScale;
  }),
  isRelView: false,
  toggleAbsRel: action((state) => {
    state.isRelView = !state.isRelView;
  }),
  xColumn: 'unemployment_abs',
  dataMap: computed(
    (state) => new Map((state.data || []).map((d) => [d.value, d]))
  ),
  filterDemographics: 'all',
  setFilterDemographics: action((state, payload) => {
    state.filterDemographics = payload;
  }),
  filterQualification: null,
  setFilterQualification: action((state, payload) => {
    state.filterQualification = payload;
  }),
  filterSector: null,
  setFilterSector: action((state, payload) => {
    state.filterSector = payload;
  }),
  isLoading: false,
  setLoading: action((state, isLoading) => {
    state.isLoading = isLoading;
  }),
  loadDataSuccess: action((state, data) => {
    state.data = data;
  }),
  loadData: thunk(async (actions) => {
    actions.setLoading(true);

    try {
      const response = await fetch('public/data/berufe.csv');
      const csv = await response.text();
      const data = csvParse(csv, parseRow);
      actions.loadDataSuccess(data);
    } catch (err) {
      console.log(err);
    }
    actions.setLoading(false);
  }),
  setValue: action((state, value) => {
    state.value = value;
  }),
  hideDefaultLabelsOnSearch: actionOn(
    (actions) => actions.setValue,
    (state) => {
      state.showDefaultLabels = false;
    }
  ),
  reset: action((state) => {
    state.value = null;
    state.filterDemographics = 'all';
    state.filterQualification = null;
    state.filterSector = null;
    state.showFullXScale = false;
  }),
};
